import { Static, Type } from '@sinclair/typebox';
import { EmailConfig, EMAIL_COMPONENT_IDS } from './email_types/EmailTypes';
import { getEmailRichTextFieldKeys } from './email_types/EmailComponentSchemaRegistry';
import { EmailPromoCodeMeta } from './email_types/EmailComponentSchemas';
import { escapeHtml } from './utils';
import { autoReplaceSmsChars } from './sms-utils';
import { isBinding } from './website_types/Bindings';
import {
  ProPromoCodeDistribution,
  ProPromoCodeTrigger,
  ProPromotionDisplayStatus,
} from './translation_namespace/pro_promo_code_namespace';

// ─── Promotion d'une campagne email / SMS (`pro_campaign.pro_promo_code_id`) ───────────────────
//
// Une campagne peut annoncer UNE promotion du pro : automatique, à code partagé ou à codes personnels.
// Ses variables `promo:*` sont résolues à l'envoi (et dans l'aperçu de l'éditeur) :
//   - `promo:code` : le code partagé ; vide pour une automatique (le bloc Code promo masque alors son
//     code et son lien part sans `?code=`) ; pour des codes personnels, le code de CHAQUE destinataire,
//     par le même chemin que `{{customer:promo_code}}` (propriété Mailjet, valeur dynamique AllMySMS) ;
//   - `promo:label` : libellé public (à défaut le nom) ; `promo:value` : « −20 % », « −10,00 € » ;
//   - `promo:ends_at` : fin de validité formatée par le serveur, vide sans date de fin.

export const PROMO_BINDING_KEYS = {
  CODE: 'promo:code',
  LABEL: 'promo:label',
  VALUE: 'promo:value',
  ENDS_AT: 'promo:ends_at',
} as const;

/** `promo:code` tel qu'il s'écrit dans le contenu d'une campagne (lien `{{pro:booking}}?code={{promo:code}}`). */
export const PROMO_CODE_TOKEN = `{{${PROMO_BINDING_KEYS.CODE}}}`;

/** Toute variable `promo:*`, connue ou non : une inconnue est vidée, jamais envoyée telle quelle. */
const PROMO_BINDING_PATTERN = /\{\{(promo:[a-z_]+)\}\}/g;

/** Valeurs des variables `promo:*` d'une campagne, déjà formatées par le serveur. */
export const CampaignPromotionBindingsSchema = Type.Object({
  code: Type.String(),
  label: Type.String(),
  value: Type.String(),
  ends_at: Type.String(),
});
export type CampaignPromotionBindingsObject = Static<typeof CampaignPromotionBindingsSchema>;

/**
 * Une promotion proposée dans le champ « Promotion » d'une campagne (`GET /app/pro-campaign/promotions`) :
 * active ou programmée, ni terminée ni épuisée — ou la promotion déjà jointe devenue inutilisable
 * (`attachable: false`, gardée pour que le champ affiche son nom).
 */
export const CampaignPromotionSchema = Type.Object({
  pro_promo_code_id: Type.Integer(),
  /** Nom interne de la promotion (liste du champ). */
  label: Type.String(),
  trigger: Type.Enum(ProPromoCodeTrigger),
  distribution: Type.Enum(ProPromoCodeDistribution),
  display_status: Type.Enum(ProPromotionDisplayStatus),
  attachable: Type.Boolean(),
  /** Valeurs des variables pour l'aperçu de l'éditeur ; codes personnels : un exemple de code. */
  preview_bindings: CampaignPromotionBindingsSchema,
  /**
   * Début de la promotion, formaté par le serveur, quand les clients ne pourront s'en servir qu'à partir de
   * cette date : promotion jugée sur la date de réservation et pas encore commencée. Absent sinon (jugée sur
   * la date du rendez-vous, un client peut déjà réserver un créneau de sa période).
   */
  usable_from_formatted: Type.Optional(Type.String()),
});
export type CampaignPromotionObject = Static<typeof CampaignPromotionSchema>;

export const CampaignPromotionListResponseSchema = Type.Object({
  items: Type.Array(CampaignPromotionSchema),
});
export type CampaignPromotionListResponseObject = Static<typeof CampaignPromotionListResponseSchema>;

/** Le texte cite-t-il une variable `promo:*` ? */
export function textUsesPromotionBindings(text: string | null | undefined): boolean {
  if (!text) return false;
  PROMO_BINDING_PATTERN.lastIndex = 0;
  return PROMO_BINDING_PATTERN.test(text);
}

/** Un bloc de l'email (ou son texte d'aperçu) cite-t-il une variable `promo:*` ? */
export function emailConfigUsesPromotionBindings(
  config: Pick<EmailConfig, 'components' | 'previewText'> | null | undefined,
): boolean {
  if (textUsesPromotionBindings(config?.previewText)) return true;
  return (config?.components ?? []).some((component): boolean =>
    Object.values(component.values ?? {}).some(
      (value: unknown): boolean => typeof value === 'string' && textUsesPromotionBindings(value),
    ),
  );
}

/**
 * Contexte où une variable `promo:*` est substituée : HTML (bloc riche, valeur échappée), texte rendu
 * par React ou objet d'email (valeur brute), SMS (valeur ramenée au jeu GSM : le signe moins « − » et
 * les espaces fines du format français feraient passer tout le SMS en Unicode, deux fois plus cher).
 */
export type PromotionBindingFormat = 'html' | 'text' | 'sms';

const formatPromotionBindingValue = (value: string, format: PromotionBindingFormat): string => {
  if (format === 'html') return escapeHtml(value);
  if (format === 'sms') return autoReplaceSmsChars(value).text;
  return value;
};

/**
 * Substitue les variables `promo:*` d'un texte. Sans promotion (`null`), elles sont vidées : l'envoi
 * refuse en amont un contenu qui en cite sans promotion jointe (CAMPAIGN_PROMO_BINDING_MISMATCH).
 */
export function resolvePromotionBindings(
  text: string,
  bindings: CampaignPromotionBindingsObject | null,
  format: PromotionBindingFormat,
): string {
  return text.replace(PROMO_BINDING_PATTERN, (_: string, key: string): string => {
    if (!bindings) return '';
    switch (key) {
      case PROMO_BINDING_KEYS.CODE:
        return formatPromotionBindingValue(bindings.code, format);
      case PROMO_BINDING_KEYS.LABEL:
        return formatPromotionBindingValue(bindings.label, format);
      case PROMO_BINDING_KEYS.VALUE:
        return formatPromotionBindingValue(bindings.value, format);
      case PROMO_BINDING_KEYS.ENDS_AT:
        return formatPromotionBindingValue(bindings.ends_at, format);
      default:
        return '';
    }
  });
}

/**
 * Variables `promo:*` d'un email substituées bloc par bloc : valeur échappée dans un champ HTML riche,
 * brute ailleurs (texte rendu par React, qui échappe déjà ; lien ; texte d'aperçu).
 */
export function resolvePromotionBindingsInEmailConfig(
  config: EmailConfig,
  bindings: CampaignPromotionBindingsObject | null,
): EmailConfig {
  return {
    ...config,
    ...(config.previewText === undefined
      ? {}
      : { previewText: resolvePromotionBindings(config.previewText, bindings, 'text') }),
    components: config.components.map((component): EmailConfig['components'][number] => {
      const richTextKeys = getEmailRichTextFieldKeys(component.componentId);
      return {
        ...component,
        values: Object.fromEntries(
          Object.entries(component.values ?? {}).map(([key, value]): [string, unknown] => [
            key,
            typeof value === 'string'
              ? resolvePromotionBindings(value, bindings, richTextKeys.has(key) ? 'html' : 'text')
              : value,
          ]),
        ),
      };
    }),
  };
}

/**
 * Choix d'une promotion dans l'éditeur d'une campagne email : un email qui ne cite encore aucune variable
 * de la promotion reçoit le bloc Code promo (avant les réseaux sociaux et le pied de page), que la
 * promotion remplit. Un email qui en cite déjà une est rendu tel quel (même objet).
 */
export function withPromotionCodeBlock(config: EmailConfig, blockId: string): EmailConfig {
  if (emailConfigUsesPromotionBindings(config)) return config;
  const block: EmailConfig['components'][number] = {
    id: blockId,
    componentId: EMAIL_COMPONENT_IDS.PROMO_CODE,
    variant: 'framed',
    values: { ...EmailPromoCodeMeta.variants.framed.defaults },
  };
  const anchor = config.components.findIndex(
    (component): boolean =>
      component.componentId === EMAIL_COMPONENT_IDS.SOCIAL || component.componentId === EMAIL_COMPONENT_IDS.FOOTER,
  );
  const index = anchor === -1 ? config.components.length : anchor;
  return {
    ...config,
    components: [...config.components.slice(0, index), block, ...config.components.slice(index)],
  };
}

/**
 * Lien qui applique le code dans le tunnel (`?code=`, appliqué sans clic). Sans code (promotion
 * automatique, aucune promotion), le lien reste tel quel. Une variable (code personnel, substitué par
 * le destinataire à l'envoi) n'est pas encodée : `encodeURIComponent` casserait sa substitution.
 */
export function appendPromoCodeToUrl(url: string, code: string | null | undefined): string {
  const trimmed = code?.trim();
  if (!url || !trimmed) return url;
  const hashIndex = url.indexOf('#');
  const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
  const value = isBinding(trimmed) ? trimmed : encodeURIComponent(trimmed);
  return `${base}${base.includes('?') ? '&' : '?'}code=${value}${hash}`;
}
